import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { marketService } from '../lib/services/marketService';
import CommunityService from '../lib/services/communityService';
import type { MarketListing, UserConnection } from '../types';
// import { Typography } from '@mui/material'; // Exemplo: Manter apenas se realmente usado em algum lugar não refatorado

// Ícones Lucide React
import { 
  ArrowLeft, 
  ShoppingCart as LucideShoppingCart, 
  Tag as LucideTag, 
  Send as LucideSend, 
  MapPin, 
  Smile, 
  FileText, 
  UserCircle, 
  Loader2, 
  UserPlus, 
  UserCheck, 
  MessageCircle, 
  AlertTriangle 
} from 'lucide-react'; 

import { Button as ShadcnButton } from '@/components/ui/button';
import { Dialog as ShadcnDialog, DialogContent, DialogFooter, DialogHeader, DialogTitle as ShadcnDialogTitle } from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
// import SEO from '../components/SEO';

const communityService = new CommunityService();

const MarketListingDetails: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  // console.log('Valor do parâmetro listingId:', listingId);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  // const muiTheme = useTheme(); // Removido
  const { theme } = useAppTheme();
  // const isMobile = useMediaQuery(muiTheme.breakpoints.down('md')); // Removido
  
  // Estados
  const [listing, setListing] = useState<MarketListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<UserConnection | null>(null);
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Carregar detalhes do anúncio
  useEffect(() => {
    const loadListingDetails = async () => {
      if (!listingId) {
        console.log('ID não encontrado nos parâmetros da URL');
        setError('ID do anúncio não fornecido');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      console.log('Iniciando carregamento do anúncio:', listingId);
      
      try {
        const data = await marketService.getListingDetails(listingId);
        console.log('Resposta do serviço:', data);
        
        if (!data) {
          console.log('Anúncio não encontrado na resposta do serviço');
          setError('Anúncio não encontrado');
          setLoading(false);
          return;
        }
        
        setListing(data);
        console.log('Anúncio carregado com sucesso:', data.id);
        
        // Verificar status de conexão entre usuários
        if (user?.id && data.user_id !== user.id) {
          setConnectionLoading(true);
          try {
            const connection = await communityService.getConnectionStatus(user.id, data.user_id);
            setConnectionStatus(connection);
          } catch (connErr) {
            console.error('Erro ao carregar status de conexão:', connErr);
            // Não bloqueia o fluxo principal se falhar
          } finally {
            setConnectionLoading(false);
          }
        }
      } catch (err) {
        console.error('Erro ao carregar detalhes do anúncio:', err);
        setError('Erro ao carregar os detalhes do anúncio. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };
    
    loadListingDetails();
  }, [listingId, user?.id]);
  
  // Formatar preço
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };
  
  // Mapear condição para texto legível
  const getConditionText = (condition?: string | null) => {
    switch (condition) {
      case 'new': return 'Novo';
      case 'like_new': return 'Como Novo';
      case 'good': return 'Bom';
      case 'fair': return 'Regular';
      case 'poor': return 'Ruim';
      default: return 'Não informado';
    }
  };

  // Mapear condição para classes Tailwind (borda e texto)
  const getConditionTailwindClasses = (condition?: string | null) => {
    switch (condition) {
      case 'new': return { border: 'border-green-500', text: 'text-green-500', bg: 'dark:bg-green-500/10 bg-green-500/5' };
      case 'like_new': return { border: 'border-lime-500', text: 'text-lime-500', bg: 'dark:bg-lime-500/10 bg-lime-500/5' };
      case 'good': return { border: 'border-yellow-500', text: 'text-yellow-500', bg: 'dark:bg-yellow-500/10 bg-yellow-500/5' };
      case 'fair': return { border: 'border-orange-500', text: 'text-orange-500', bg: 'dark:bg-orange-500/10 bg-orange-500/5' };
      case 'poor': return { border: 'border-red-500', text: 'text-red-500', bg: 'dark:bg-red-500/10 bg-red-500/5' };
      default: return { border: 'border-gray-400', text: 'text-gray-400', bg: 'dark:bg-gray-500/10 bg-gray-500/5' };
    }
  };
  
  // Botão voltar ao mercado
  const handleBackToMarket = () => {
    navigate('/market');
  };
  
  // Enviar solicitação de conexão
  const handleSendConnectionRequest = async () => {
    if (!user?.id || !listing?.user_id) return;
    
    setConnectionLoading(true);
    
    try {
      const result = await communityService.sendConnectionRequest(user.id, listing.user_id);
      
      if (result.success) {
        // Recarregar status de conexão
        const connection = await communityService.getConnectionStatus(user.id, listing.user_id);
        setConnectionStatus(connection);
        alert('Solicitação de conexão enviada com sucesso!');
      } else {
        alert(`Erro ao enviar solicitação: ${result.error}`);
      }
    } catch (err) {
      console.error('Erro ao enviar solicitação de conexão:', err);
      alert('Erro ao enviar solicitação de conexão. Tente novamente.');
    } finally {
      setConnectionLoading(false);
    }
  };
  
  // Enviar mensagem
  const handleSendMessage = async () => {
    if (!user?.id || !listing?.user_id || !message.trim()) {
      setMessageError('Digite uma mensagem válida');
      return;
    }
    
    setSendingMessage(true);
    setMessageError(null);
    
    try {
      const result = await communityService.sendMessage(user.id, listing.user_id, message.trim());
      
      if (result.success) {
        setMessageDialogOpen(false);
        alert('Mensagem enviada com sucesso!');
        // Redirecionar para a conversa
        if (listing.user_profile?.username) {
          navigate(`/messages/${listing.user_profile.username}`);
        }
      } else {
        setMessageError(result.error || 'Erro ao enviar mensagem');
      }
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setMessageError('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setSendingMessage(false);
    }
  };
  
  // Navegar para o perfil do usuário
  const handleViewProfile = () => {
    if (listing?.user_profile?.username) {
      navigate(`/profile/${listing.user_profile.username}`);
    }
  };
  
  // Renderizar conteúdo do botão de conexão
  const renderConnectionButton = () => {
    if (!user || user?.id === listing?.user_id) {
      return null;
    }
    
    if (connectionLoading) {
      return (
        <ShadcnButton 
          disabled 
          className="w-full rounded-md py-2.5 mb-4 flex items-center justify-center"
        >
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Carregando...
        </ShadcnButton>
      );
    }
    
    if (!connectionStatus) {
      return (
        <ShadcnButton
          variant="outline"
          onClick={handleSendConnectionRequest}
          className={`w-full rounded-md py-2.5 mb-4 flex items-center justify-center font-medium 
                      ${theme === 'dark' 
                        ? 'border-primary-400 text-primary-400 hover:bg-primary-400/10' 
                        : 'border-primary-600 text-primary-600 hover:bg-primary-600/10'}`}
        >
          <UserPlus className="mr-2 h-5 w-5" />
          Adicionar como Amigo
        </ShadcnButton>
      );
    }
    
    if (connectionStatus.status === 'pending') {
      const userSentRequest = connectionStatus.user_id === user.id;
      
      if (userSentRequest) {
        return (
          <ShadcnButton 
            disabled 
            variant="outline" 
            className={`w-full rounded-md py-2.5 mb-4 flex items-center justify-center 
                        ${theme === 'dark' ? 'text-gray-500 border-gray-700' : 'text-gray-400 border-gray-300'}`}
          >
            Solicitação Enviada
          </ShadcnButton>
        );
      } else {
        return (
          <ShadcnButton
            variant="default"
            onClick={() => alert('Use a página de conexões para aceitar esta solicitação')}
            className={`w-full rounded-md py-2.5 mb-4 flex items-center justify-center font-semibold shadow-md 
                        ${theme === 'dark' 
                          ? 'bg-primary-500 hover:bg-primary-600 text-white' 
                          : 'bg-primary-600 hover:bg-primary-700 text-white'}`}
          >
            <UserCheck className="mr-2 h-5 w-5" />
            Aceitar Solicitação
          </ShadcnButton>
        );
      }
    }
    
    if (connectionStatus.status === 'accepted') {
      return (
        <ShadcnButton
          variant="default"
          onClick={() => setMessageDialogOpen(true)}
          className={`w-full rounded-md py-2.5 mb-4 flex items-center justify-center font-semibold shadow-lg 
                      ${theme === 'dark' 
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white' 
                        : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'}`}
        >
          <MessageCircle className="mr-2 h-5 w-5" />
          Enviar Mensagem
        </ShadcnButton>
      );
    }
    
    return null;
  };
  
  // Excluir anúncio (para administradores)
  const handleAdminDelete = async () => {
    if (!user?.id || !listingId) return;
    
    setDeleting(true);
    
    try {
      console.log('Iniciando exclusão do anúncio pelo admin:', listingId);
      const result = await marketService.deleteListing(listingId, user.id, user.is_admin);
      
      if (result.success) {
        setDeleteDialogOpen(false);
        alert('Anúncio excluído com sucesso! Você será redirecionado para a página do mercado.');
        
        // Redirecionar para o mercado após um breve delay para garantir que o usuário veja a mensagem
        setTimeout(() => {
          navigate('/market');
        }, 1000);
      } else {
        console.error('Falha ao excluir anúncio:', result.error);
        alert(`Erro ao excluir anúncio: ${result.error || 'Erro desconhecido'}. Como você é administrador, o anúncio foi marcado como "excluído", mas continua no banco de dados.`);
        
        // Redirecionar mesmo com erro, pois o anúncio foi marcado como excluído
        setTimeout(() => {
          navigate('/market');
        }, 1500);
      }
    } catch (err) {
      console.error('Erro ao excluir anúncio:', err);
      alert('Erro ao excluir anúncio. Tente novamente mais tarde.');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-10 flex justify-center flex-col items-center min-h-[calc(100vh-128px)] dark:bg-gray-900">
        <Loader2 className={`h-16 w-16 animate-spin ${theme === 'dark' ? 'text-primary-400' : 'text-primary-600'}`} />
        <p className={`mt-4 text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
          Carregando detalhes do anúncio...
        </p>
      </div>
    );
  }
  
  if (error || !listing) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-10 dark:bg-gray-900 min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className={`p-6 md:p-8 rounded-lg shadow-xl w-full max-w-lg text-center ${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
          <div className={`mb-4 p-4 rounded-md border ${theme === 'dark' ? 'bg-red-900/30 border-red-700 text-red-300' : 'bg-red-50 border-red-400 text-red-700'}`}>
            <div className="flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-semibold">Erro</h3>
            </div>
            <p className="mt-1 text-sm">{error || 'Anúncio não encontrado'}</p>
          </div>
          <ShadcnButton
            variant="default"
            onClick={handleBackToMarket}
            className={`rounded-md ${theme === 'dark' ? 'bg-primary-600 hover:bg-primary-700' : 'bg-primary-600 hover:bg-primary-700 text-white'}`}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar ao Mercado
          </ShadcnButton>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Removido o componente SEO temporariamente */}
      <div className={`max-w-6xl mx-auto px-4 py-6 md:py-10 min-h-[calc(100vh-64px)] ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <ShadcnButton
          variant="outline"
          onClick={handleBackToMarket}
          className={`mb-6 rounded-md px-3 py-2 text-sm 
                      ${theme === 'dark' 
                        ? 'text-gray-300 border-gray-600 hover:border-gray-500 hover:bg-gray-700'
                        : 'text-primary-main border-primary-main hover:bg-primary-main/10'}`}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar ao Mercado
        </ShadcnButton>
        
        {/* Card Header Refatorado */}
        <div className={`rounded-lg shadow-md overflow-hidden mb-8 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
          <div
            className={`p-4 md:p-6 text-white 
              ${listing.listing_type === 'sell'
                ? (theme === 'dark' ? 'bg-gradient-to-r from-blue-600 to-indigo-700' : 'bg-gradient-to-r from-indigo-600 to-blue-700')
                : (theme === 'dark' ? 'bg-gradient-to-r from-pink-500 to-purple-600' : 'bg-gradient-to-r from-purple-500 to-pink-600')}`}
          >
            <div className="flex items-center space-x-4">
              <div>
                {listing.listing_type === 'sell' ? (
                  <LucideTag className="h-7 w-7 md:h-9 md:w-9" />
                ) : (
                  <LucideShoppingCart className="h-7 w-7 md:h-9 md:w-9" />
                )}
              </div>
              <div className="flex-grow">
                <h1 className="text-2xl md:text-3xl font-bold">
                  {listing.listing_type === 'sell' ? 'Anúncio de Venda' : 'Anúncio de Compra'}
                </h1>
                <p className="text-sm opacity-90">
                  Publicado em {new Date(listing.created_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Layout principal em columns Refatorado */}
        <div className="md:flex md:space-x-8 space-y-8 md:space-y-0">
          {/* Coluna da esquerda - Informações do jogo */}
          <div className="md:w-7/12 w-full">
            <div className={`rounded-lg shadow-md overflow-hidden h-full ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`flex justify-center items-center p-6 min-h-[200px] md:min-h-[300px] ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <img
                  src={listing.game?.image_url || '/placeholder-game.jpg'}
                  alt={listing.game?.name || 'Jogo'}
                  className="max-w-full max-h-[350px] object-contain rounded-lg shadow-lg"
                />
              </div>
              
              <div className="p-6">
                <div className="sm:flex sm:justify-between sm:items-center mb-6 block sm:space-y-0 space-y-4">
                  <h2 className={`text-2xl md:text-3xl font-bold ${theme === 'dark' ? 'text-primary-400' : 'text-primary-600'}`}>
                    {listing.game?.name || 'Jogo'}
                  </h2>
                  
                  <div className={`px-3 py-1 text-white rounded-lg shadow-sm text-center min-w-full sm:min-w-0 ${theme === 'dark' ? 'bg-green-700' : 'bg-green-600'}`}>
                    <p className="text-base md:text-lg font-bold">
                      {formatPrice(listing.price ?? 0)}
                    </p>
                  </div>
                </div>
                
                {/* Condição e Localização Refatorados */}
                <div className="sm:flex sm:space-x-6 space-y-4 sm:space-y-0 mb-6">
                  {listing.condition && (
                    <div className="sm:w-1/2 w-full">
                      <div className={`p-3 rounded-lg border-2 h-full ${getConditionTailwindClasses(listing.condition).border} ${getConditionTailwindClasses(listing.condition).bg}`}>
                        <div className="flex items-center space-x-2">
                          <Smile className={`h-6 w-6 ${getConditionTailwindClasses(listing.condition).text}`} />
                          <div>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              Condição
                            </p>
                            <p className={`font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>
                              {getConditionText(listing.condition)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {listing.location && (
                    <div className="sm:w-1/2 w-full">
                      <div className={`p-3 rounded-lg border-2 h-full ${theme === 'dark' ? 'border-gray-600 bg-gray-700/50' : 'border-gray-300 bg-gray-50/50'}`}>
                        <div className="flex items-center space-x-2">
                          <MapPin className={`h-6 w-6 ${theme === 'dark' ? 'text-primary-400' : 'text-primary-600'}`} />
                          <div>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              Localização
                            </p>
                            <p className={`font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>
                              {listing.location}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {listing.description && (
                  <div className="mt-6">
                    <h3 className={`text-lg font-semibold flex items-center mb-2 ${theme === 'dark' ? 'text-primary-300' : 'text-primary-700'}`}>
                      <FileText className="mr-2 h-5 w-5" />
                      Descrição
                    </h3>
                    <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-100 border-gray-200'}`}>
                      <p className={`whitespace-pre-wrap ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {listing.description}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Coluna da direita - Vendedor e ações */}
          <div className="md:w-5/12 w-full">
            <div className="space-y-6">
              {/* Card do vendedor */}
              <div className={`rounded-lg shadow-md overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className={`p-4 border-b ${theme === 'dark' ? 'bg-gray-750 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
                  <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>
                    {listing.listing_type === 'sell' ? 'Sobre o Vendedor' : 'Sobre o Comprador'}
                  </h3>
                </div>
                
                <div className="p-6">
                  {listing.user_profile && (
                    <div className="sm:flex sm:items-center sm:space-x-4 block sm:space-y-0 space-y-3 text-center sm:text-left">
                      <img 
                        src={listing.user_profile.avatar_url || 'https://via.placeholder.com/150/000000/FFFFFF/?text=User'} 
                        alt={listing.user_profile.username || 'Usuário'}
                        className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full shadow-md mx-auto sm:mx-0 ${theme === 'dark' ? 'border-2 border-gray-700' : 'border-2 border-white'}`}
                      />
                      <div>
                        <h4 className={`text-xl font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>
                          {listing.user_profile.username || 'Usuário Desconhecido'}
                        </h4>
                        <ShadcnButton 
                          variant="outline"
                          size="sm"
                          onClick={handleViewProfile}
                          className={`mt-1 rounded-full text-xs px-3 py-1 
                                      ${theme === 'dark' 
                                        ? 'text-primary-300 border-primary-300 hover:bg-primary-300/10' 
                                        : 'text-primary-600 border-primary-600 hover:bg-primary-600/10'}`}
                        >
                          <UserCircle className="mr-1.5 h-4 w-4" />
                          Ver Perfil
                        </ShadcnButton>
                      </div>
                    </div>
                  )}
                  
                  <hr className={`my-6 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`} />
                  
                  {/* Botões de ação Refatorados */}
                  <div className="space-y-3">
                    {renderConnectionButton()}
                    
                    {listing.listing_type === 'sell' && (
                      <ShadcnButton
                        variant="default"
                        onClick={handleViewProfile} // TODO: Deve ser uma ação de interesse, não ver perfil. Ajustar depois.
                        className={`w-full rounded-md py-3 text-base font-semibold shadow-md 
                                    ${theme === 'dark' 
                                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                                      : 'bg-green-500 hover:bg-green-600 text-white'}`}
                      >
                        Tenho Interesse em Comprar
                      </ShadcnButton>
                    )}
                    
                    {/* Botão de exclusão para administradores */}
                    {user?.is_admin && (
                      <ShadcnButton
                        variant="outline"
                        onClick={() => setDeleteDialogOpen(true)}
                        className={`w-full rounded-md py-2.5 font-medium border-2 
                                    ${theme === 'dark' 
                                      ? 'text-red-400 border-red-400 hover:bg-red-400/10' 
                                      : 'text-red-600 border-red-600 hover:bg-red-600/10'}`}
                      >
                        Excluir Anúncio (Admin)
                      </ShadcnButton>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Card de segurança Refatorado*/}
              <div className={`rounded-lg border-2 p-4 md:p-5 
                            ${theme === 'dark' 
                              ? 'border-yellow-500/60 bg-yellow-700/20' 
                              : 'border-yellow-400/80 bg-yellow-500/10'}`}>
                <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-3 sm:gap-4">
                  <AlertTriangle className={`h-8 w-8 sm:h-7 sm:w-7 flex-shrink-0 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500'}`} />
                  <div>
                    <h4 className={`text-base font-semibold mb-1.5 ${theme === 'dark' ? 'text-yellow-300' : 'text-yellow-700'}`}>
                      Recomendações de Segurança
                    </h4>
                    <ul className={`text-xs list-disc list-inside space-y-1 ${theme === 'dark' ? 'text-yellow-200/90' : 'text-yellow-600/90'}`}>
                      <li>Verifique o perfil do usuário antes de negociar</li>
                      <li>Prefira locais públicos para encontros</li>
                      <li>O Boardverso não se responsabiliza pelas transações</li>
                      <li>Nunca faça transferências sem garantias</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Diálogo de Mensagem Refatorado com Shadcn/UI */}
        <ShadcnDialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
          <DialogContent className={`sm:max-w-[525px] rounded-lg ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-50' : 'bg-white'}`}>
            <DialogHeader className="pb-3">
              <ShadcnDialogTitle className={`text-lg font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                Enviar Mensagem para {listing?.user_profile?.username || 'Usuário'}
              </ShadcnDialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="message-text" className={`sr-only ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Sua mensagem
                </Label>
                <Textarea
                  id="message-text"
                  placeholder="Digite sua mensagem aqui..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-50 focus:border-primary-500' : 'focus:border-primary-500'}`}
                />
                {messageError && (
                  <p className="text-sm text-red-500 dark:text-red-400 mt-1">{messageError}</p>
                )}
              </div>
            </div>
            <DialogFooter className={`pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <ShadcnButton 
                variant="outline"
                onClick={() => setMessageDialogOpen(false)}
                className={`${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : ''}`}
              >
                Cancelar
              </ShadcnButton>
              <ShadcnButton 
                type="submit" 
                onClick={handleSendMessage}
                disabled={sendingMessage}
                className={`${theme === 'dark' ? 'bg-primary-600 hover:bg-primary-700 text-white' : 'bg-primary-600 hover:bg-primary-700 text-white'}`}
              >
                {sendingMessage ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <LucideSend className="mr-2 h-4 w-4" />
                )}
                Enviar
              </ShadcnButton>
            </DialogFooter>
          </DialogContent>
        </ShadcnDialog>
        
        {/* Diálogo de Exclusão de Anúncio (Admin) Refatorado com Shadcn/UI */}
        <ShadcnDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className={`sm:max-w-md rounded-lg ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-50' : 'bg-white'}`}>
            <DialogHeader className="pb-3">
              <ShadcnDialogTitle className={`text-lg font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                Confirmar Exclusão de Anúncio
              </ShadcnDialogTitle>
            </DialogHeader>
            <div className="py-2">
              <div className={`flex items-start p-3 rounded-md mb-4 ${theme === 'dark' ? 'bg-yellow-700/30' : 'bg-yellow-500/10'}`}>
                <AlertTriangle className={`h-6 w-6 flex-shrink-0 mr-3 mt-0.5 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`} />
                <div>
                  <h4 className={`text-sm font-semibold mb-1 ${theme === 'dark' ? 'text-yellow-300' : 'text-yellow-700'}`}>
                    Atenção
                  </h4>
                  <p className={`text-sm ${theme === 'dark' ? 'text-yellow-200/90' : 'text-yellow-600/90'}`}>
                    Esta ação não pode ser desfeita. Tem certeza que deseja excluir este anúncio?
                  </p>
                </div>
              </div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                <strong>Anúncio:</strong> {listing?.game?.name} ({listing?.listing_type === 'sell' ? 'Venda' : 'Compra'})
              </p>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                <strong>Usuário:</strong> {listing?.user_profile?.username}
              </p>
            </div>
            <DialogFooter className={`pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <ShadcnButton 
                variant="outline" 
                onClick={() => setDeleteDialogOpen(false)}
                className={`${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : ''}`}
              >
                Cancelar
              </ShadcnButton>
              <ShadcnButton 
                variant="destructive" // Usar a variante destructive do Shadcn para botões de exclusão
                onClick={handleAdminDelete} 
                disabled={deleting}
                className={`${theme === 'dark' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}
              >
                {deleting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {deleting ? 'Excluindo...' : 'Excluir Anúncio'}
              </ShadcnButton>
            </DialogFooter>
          </DialogContent>
        </ShadcnDialog>
      </div>
    </>
  );
};

export default MarketListingDetails;